import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AssistantProductCard } from '@/components/assistant/AssistantProductCard';
import { assistantLineKey } from '@/lib/assistant/assistantLineKeys';

/** Altura aproximada de una tarjeta + gap (px) para mostrar N filas visibles. */
const CARD_SLOT_PX = 140;
const CARD_GAP_PX = 12;

export function AssistantProductScrollList({
  products,
  messageIndex,
  visibleCount = 3,
  primaryColor,
  business,
  benefitsProgressSnapshot,
  getAssistantLineQty,
  bumpAssistantLineQty,
  getCartQtyForProductId,
  onCatalogProductAdd,
  message,
}) {
  const scrollRef = useRef(null);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const maxHeightPx = visibleCount * CARD_SLOT_PX + Math.max(0, visibleCount - 1) * CARD_GAP_PX;

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight > el.clientHeight + 4;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setShowBottomFade(scrollable && !atBottom);
  }, []);

  useEffect(() => {
    updateFade();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(updateFade);
    ro.observe(el);
    return () => ro.disconnect();
  }, [products.length, updateFade]);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="grid grid-cols-1 gap-3 overflow-y-auto overscroll-y-contain pr-1 -mr-1"
        style={{ maxHeight: maxHeightPx, WebkitOverflowScrolling: 'touch' }}
        onScroll={updateFade}
        role="region"
        aria-label="Lista de productos"
        data-testid="assistant-product-scroll-list"
      >
        {products.map((product, prodIndex) => {
          const isWelcomeClubReward = product.assistantWelcomeKind === 'club_reward';
          const maxRewardQty = isWelcomeClubReward
            ? Math.max(0, Math.min(99, Number(benefitsProgressSnapshot?.available_rewards) || 0))
            : 99;
          const isConfirmation = message.productCardMode === 'confirmation';
          const lineKey = assistantLineKey(messageIndex, product.id);
          const lineQty = getAssistantLineQty(lineKey);
          return (
            <AssistantProductCard
              key={product.id ?? prodIndex}
              product={product}
              primaryColor={primaryColor}
              lineQty={lineQty}
              inCartQty={getCartQtyForProductId(product.id)}
              onBumpQty={(delta) => bumpAssistantLineQty(lineKey, delta)}
              isConfirmation={isConfirmation}
              isWelcomeClubReward={isWelcomeClubReward}
              maxRewardQty={maxRewardQty}
              business={business}
              testId={`add-to-cart-${product.id || prodIndex}`}
              addLabel={isWelcomeClubReward ? 'Canjear' : 'Agregar'}
              onAdd={() =>
                onCatalogProductAdd({
                  product,
                  message,
                  lineQty,
                  isWelcomeClubReward,
                })
              }
            />
          );
        })}
      </div>
      {showBottomFade ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background via-background/80 to-transparent"
          aria-hidden
        />
      ) : null}
    </div>
  );
}
